import type { FC } from 'react'
import React, { useReducer, useEffect, useState } from 'react'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import clsx from 'clsx'
import InnerHTML from 'dangerously-set-html-content'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import useMediaQuery from '@material-ui/core/useMediaQuery'

import {
  getUser,
  useGetWidgetTypesQuery,
  useGetWidgetQuery,
  useGetWidgetDevelopmentsQuery,
  useCreateWidgetMutation,
  useGenerateWidgetMutation,
  widgetsApi,
} from '@homeviews/store'
import { mixpanelTrack } from '@homeviews/seo'
import {
  Typography,
  Card,
  Button,
  Stepper,
  TextField,
  Select,
  Checkbox,
  Snackbar,
  Spinner,
  Alert,
} from '@homeviews/shared-ui'
import type { IWidgetType } from '../../helpers/WidgetTypes'
import {
  WIDGET_TYPES,
  WIDGET_STEPS,
  getDevelopmentOptions,
} from '../../helpers/WidgetTypes'
import WidgetCard from '../WidgetCard'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
      '& h1': {
        marginBottom: 0,
      },
    },
    backButton: {
      marginRight: 6,
    },
    widgetDetails: {
      display: 'flex',
      alignItems: 'flex-end',
    },
    stepperWrapper: {
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    buttonsWrapper: {
      display: 'flex',
    },
    previewWrapper: {},
    previewWrapperSidebar: {
      maxWidth: 300,
      minHeight: 500,
      '& > *': {
        height: '100%',
      },
    },
    copyWidgetButtonWrapper: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: 20,
    },
    widgetCodeWrapper: {
      border: '1px solid #4E516B',
      borderRadius: 5,
      boxShadow: '0 1px 3px 0 rgba(46,81,123,0.04)',
      padding: theme.spacing(1.25, 1.75),
      overflowWrap: 'break-word',
    },
    permissionAlert: {
      marginBottom: 24,
    },
  }),
)

interface IState {
  step: number
  selectedWidget: string
  previewLayout: 'sidebar' | 'full'
  development?: number | null
  url: string
  widgetID: string
  widgetCode: string
  previewWidgetCode: string
  previewMode?: boolean
}

const initialState: IState = {
  step: 1,
  selectedWidget: '',
  previewLayout: 'full',
  development: null,
  url: '',
  widgetID: '',
  widgetCode: '',
  previewWidgetCode: '',
  previewMode: false,
}

const WidgetBuilder: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector(getUser)
  const theme = useTheme()

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [state, setState] = useReducer(
    (state: IState, newState: Partial<IState>) => ({ ...state, ...newState }),
    initialState,
  )

  const { id: widgetIdFromUrl } = useParams<{ id: string }>()

  let widgetID = state?.widgetID

  if (widgetIdFromUrl) {
    widgetID = widgetIdFromUrl
  }

  const { data: initialPreviewData, isFetching: isFetchingWidget } =
    useGetWidgetQuery(
      {
        id: widgetID ? parseInt(widgetID) : null,
        developer: user?.developer_id,
        layout: state?.previewLayout,
      },
      {
        skip: !widgetID || !user?.developer_id,
      },
    )

  useEffect(() => {
    setState({
      ...state,
      widgetID: widgetID,
      widgetCode: initialPreviewData?.code,
      step: widgetIdFromUrl ? 3 : state.step,
      previewMode: !!widgetIdFromUrl,
      previewWidgetCode: initialPreviewData?.code || '',
    })

    const initialPreviewWidgetData: any = initialPreviewData

    if (widgetIdFromUrl && initialPreviewWidgetData?.widget?.id) {
      const eventPayload: any = {
        'Destination URL': initialPreviewWidgetData?.widget?.website,
      }

      if (state.selectedWidget !== 'company-widget') {
        eventPayload['Development Name'] =
          initialPreviewWidgetData?.widget?.title || ''
      }

      const eventWidgetName = WIDGET_TYPES.find(
        (widgetType: IWidgetType) =>
          widgetType.id ===
          initialPreviewWidgetData?.widget?.widget_type,
      )?.title

      eventPayload['Widget Type'] = eventWidgetName || ''

      mixpanelTrack('WIDGETS_VIEW_WIDGET', eventPayload)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPreviewData, widgetIdFromUrl])

  const [copyAlertVisible, setCopyAlertVisible] = useState<boolean>(false)
  const [snackbarVisible, setSnackbarVisible] = useState<{
    open: boolean
    message: string
    status?: 'success' | 'error'
  }>({
    open: false,
    message: '',
    status: undefined,
  })

  const [generateWidget, { isLoading }] = useGenerateWidgetMutation()
  const [createWidget, { isLoading: isCreating }] = useCreateWidgetMutation()

  const { data: widgetTypesResponse, isLoading: isLoadingWidgetTypes } =
    useGetWidgetTypesQuery(user?.developer_id || '')

  const {
    data: allowedDevelopments,
    error,
    isFetching,
  } = useGetWidgetDevelopmentsQuery(user?.developer_id || '')

  const handleWidgetCardClick = (id: string) => {
    setState({
      ...state,
      selectedWidget: state?.selectedWidget === id ? '' : id,
      step: isMobile ? state.step + 1 : state.step,
    })

    // const eventWidgetName = WIDGET_TYPES.find(
    //   (widgetType: IWidgetType) => widgetType.id === id
    // )?.title

    // if (eventWidgetName) {
    //   mixpanelTrack('WIDGETS_CREATE_WIDGET', {
    //     'Widget Type': eventWidgetName
    //   })
    // }
  }

  const handleNextStepClick = () => {
    setState({
      ...state,
      step: state.step + 1,
    })
  }

  const handlePrevStepClick = () => {
    const newStep = state.step - 1
    setState({
      ...state,
      previewLayout: 'full',
      development: null,
      url: '',
      widgetID: '',
      widgetCode: '',
      previewWidgetCode: '',
      step: newStep < 1 ? 1 : newStep,
    })
  }

  const handleFieldChange =
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setState({
        ...state,
        [name]: value,
      })

      if ('previewLayout' === name) {
        const previewQueryParams = {
          id: widgetID ? parseInt(widgetID) : null,
          developer: user?.developer_id,
          layout: value,
        }
        dispatch(
          widgetsApi.endpoints.getWidget.initiate(previewQueryParams, {
            forceRefetch: true,
          }),
        )
      }
    }

  const handleSelectChange =
    (name: string) => (newOption: { label: string; value: number }) => {
      setState({
        ...state,
        [name]: newOption?.value || null,
      })
    }

  const handleBackToWidgetsClick = () => {
    history.push('/widgets')
  }

  const handleGenerateWidgetClick = async (action: 'generate' | 'preview') => {
    const payload: any = {
      website: state.url,
      widget_type: state.selectedWidget,
    }

    const preview = action === 'preview'

    const eventPayload: any = {
      'Destination URL': payload.website,
    }

    if (state.selectedWidget !== 'company-widget') {
      payload.development_id = state.development

      const devOption = developmentOptions.find(
        (dev: any) => state.development === dev.value,
      )
      eventPayload['Development Name'] = devOption?.label || ''
    }

    const eventWidgetName = WIDGET_TYPES.find(
      (widgetType: IWidgetType) => widgetType.id === state.selectedWidget,
    )?.title

    eventPayload['Widget Type'] = eventWidgetName || ''

    try {
      let respData: any = {}
      if (preview && !state.widgetID) {
        respData = await createWidget({
          developer: user?.developer_id,
          data: payload,
        }).unwrap()
        mixpanelTrack('WIDGETS_CREATE_WIDGET', eventPayload)
      } else if (preview && state.widgetID) {
        // Update currently previewed widget
        payload.status = 'draft'
        respData = await generateWidget({
          developer: user?.developer_id,
          data: payload,
          widgetId: state.widgetID,
        }).unwrap()
      } else if (state.widgetID) {
        payload.status = 'publish'
        respData = await generateWidget({
          developer: user?.developer_id,
          data: payload,
          widgetId: state.widgetID,
        }).unwrap()

        mixpanelTrack('WIDGETS_GENERATE_WIDGET', eventPayload)
      } else {
        payload.status = 'publish'
        respData = await createWidget({
          developer: user?.developer_id,
          data: payload,
        }).unwrap()

        mixpanelTrack('WIDGETS_GENERATE_WIDGET', eventPayload)
      }

      const resolvedWidgetID = respData?.widget?.ID || state.widgetID
      const previewQueryParams = {
        id: resolvedWidgetID ? parseInt(String(resolvedWidgetID)) : null,
        developer: user?.developer_id,
        layout: state.previewLayout,
      }
      dispatch(
        widgetsApi.endpoints.getWidget.initiate(previewQueryParams, {
          forceRefetch: true,
        }),
      )

      setState({
        ...state,
        step: action === 'preview' ? 2 : 3,
        widgetCode: respData?.code || '',
        previewWidgetCode: respData?.code || '',
        widgetID: resolvedWidgetID || '',
      })
    } catch (err) {
      setSnackbarVisible({
        open: true,
        message: 'Oops, something went wrong',
        status: 'error',
      })
    }
  }

  const validateGenerateWidget = () => {
    if (state.selectedWidget === 'company-widget') {
      return !!state.url
    }

    return state.development && state.url ? true : false
  }

  const toggleCopyAlert = (show = true) => {
    mixpanelTrack('WIDGETS_COPY_WIDGET')
    setCopyAlertVisible(show)
  }

  const handleAlertClose = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setCopyAlertVisible(false)
  }

  const handleSnackbarClose = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setSnackbarVisible({
      open: false,
      message: snackbarVisible?.message,
      status: snackbarVisible?.status,
    })
  }

  const developmentOptions = getDevelopmentOptions(allowedDevelopments)

  const previewCard = () => (
    <Card header={<Typography variant="h3">Preview</Typography>}>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={state.previewLayout === 'sidebar'}
              onChange={handleFieldChange('previewLayout')}
              name="sidebar"
              value="sidebar"
            />
          }
          label="Sidebar"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={state.previewLayout === 'full'}
              onChange={handleFieldChange('previewLayout')}
              name="full"
              value="full"
            />
          }
          label="Full-width"
        />
      </FormGroup>
      {state.previewWidgetCode && (
        <div
          className={clsx(
            classes.previewWrapper,
            state.previewLayout === 'sidebar' && classes.previewWrapperSidebar,
          )}
        >
          {isLoading && (
            <div>
              <Spinner />
            </div>
          )}
          <InnerHTML html={state.previewWidgetCode} />
        </div>
      )}
    </Card>
  )

  if (isFetchingWidget) {
    return <Spinner />
  }

  if (state.step === 1) {
    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h1">Create Widget</Typography>
          <div className={classes.stepperWrapper}>
            <Stepper steps={WIDGET_STEPS} currentStep={state?.step} />
          </div>
          <Button
            variant="contained"
            color="primary"
            customColor="primary"
            onClick={handleNextStepClick}
            disabled={!state.selectedWidget}
          >
            Create widget
          </Button>
        </div>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card header={<Typography variant="h3">Select Widget</Typography>}>
              {user?.id &&
                !isLoadingWidgetTypes &&
                !Object.keys(widgetTypesResponse || {})?.length && (
                  <Alert
                    severity="error"
                    title="Permissions not set"
                    className={classes.permissionAlert}
                  >
                    <Typography className="c-alert__message">
                      Unfortunately you do not have the correct permissions to
                      be able to access this feature. Please contact HomeViews
                      to discuss gaining access.
                    </Typography>
                  </Alert>
                )}
              <Grid container spacing={3}>
                {WIDGET_TYPES.map((widgetType: IWidgetType) => {
                  if (!widgetTypesResponse?.[widgetType.id]) {
                    return false
                  }
                  return (
                    <Grid
                      key={widgetType.id}
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      xl={2}
                    >
                      <WidgetCard
                        {...widgetType}
                        selected={state?.selectedWidget === widgetType.id}
                        handleWidgetSelect={handleWidgetCardClick}
                      />
                    </Grid>
                  )
                })}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </div>
    )
  }

  if (state.step === 2) {
    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h1">Setup Widget</Typography>
          <div className={classes.stepperWrapper}>
            <Stepper steps={WIDGET_STEPS} currentStep={2} />
          </div>
          <div className={classes.buttonsWrapper}>
            <Button
              variant="outlined"
              customVariant="outlinedPrimary"
              onClick={handlePrevStepClick}
              className={classes.backButton}
            >
              Back to Select Widget
            </Button>
            <Button
              variant="contained"
              color="primary"
              customColor="primary"
              onClick={() => handleGenerateWidgetClick('generate')}
              disabled={!validateGenerateWidget()}
            >
              Generate widget
            </Button>
          </div>
        </div>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              header={
                <Typography variant="h3">
                  Select content and destination
                </Typography>
              }
            >
              <Grid container spacing={3} className={classes.widgetDetails}>
                <Grid item xs={12} sm={6} md={3}>
                  <div className={`l-fieldWrapper`}>
                    <Select
                      placeholder="Select development"
                      options={developmentOptions}
                      value={state.development}
                      onChange={handleSelectChange('development')}
                      label="Development"
                      name="development"
                      isDisabled={state.selectedWidget === 'company-widget'}
                      isClearable
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <div className={`l-fieldWrapper`}>
                    <TextField
                      label="Destination URL*"
                      value={state.url}
                      data-id="t-url"
                      id="url"
                      name="url"
                      onChange={handleFieldChange('url')}
                      fullWidth
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <div className={`l-fieldWrapper`}>
                    <Button
                      variant="outlined"
                      customColor="dark"
                      onClick={() => handleGenerateWidgetClick('preview')}
                      disabled={!validateGenerateWidget()}
                    >
                      Preview widget
                    </Button>
                  </div>
                </Grid>
              </Grid>
              <Typography>
                *Destination URL - website where the widget will be displayed,
                e.g. https://www.example.com
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12}>
            {previewCard()}
          </Grid>
        </Grid>
        <Snackbar
          open={snackbarVisible?.open}
          onClose={handleSnackbarClose}
          alertMessage={snackbarVisible?.message}
          status={snackbarVisible?.status}
        />
      </div>
    )
  }

  if (state.step === 3) {
    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h1">Widget Export</Typography>
          <div className={classes.stepperWrapper}>
            <Stepper steps={WIDGET_STEPS} currentStep={3} />
          </div>
          <div className={classes.buttonsWrapper}>
            <Button
              variant="outlined"
              customVariant="outlinedPrimary"
              onClick={handleBackToWidgetsClick}
              className={classes.backButton}
            >
              Back to widgets
            </Button>
          </div>
        </div>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card header={<Typography variant="h3">Widget code</Typography>}>
              <Typography className={classes.widgetCodeWrapper}>
                {state.widgetCode}
              </Typography>
              <div className={classes.copyWidgetButtonWrapper}>
                {!!state.widgetCode && (
                  <CopyToClipboard
                    text={state.widgetCode}
                    onCopy={() => toggleCopyAlert()}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      customColor="primary"
                    >
                      Copy code to clipboard
                    </Button>
                  </CopyToClipboard>
                )}
              </div>
            </Card>
          </Grid>
          <Grid item xs={12}>
            {previewCard()}
          </Grid>
        </Grid>
        <Snackbar
          open={copyAlertVisible}
          onClose={handleAlertClose}
          alertMessage="Widget code copied"
        />
      </div>
    )
  }

  return <div />
}

export default WidgetBuilder
